import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useSignIn, useClerk, useAuth } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { getSafeLoginRedirect } from '@/lib/login-redirect-target';
import { InvitationSessionConflictScreen } from '@/components/auth/invitation-session-conflict-screen';
import { InvitationSignUpForm } from '@/components/auth/invitation-sign-up-form';
import { SignInFlow } from '@/components/auth/sign-in-flow';
import { WorkspaceRecoveryScreen } from '@/components/auth/workspace-recovery-screen';

/** Search params for login page - handles Clerk invitation flow and redirect */
type LoginSearchParams = {
  __clerk_status?: string;
  __clerk_ticket?: string;
  orgResolutionFailed?: string;
  redirect?: string;
};

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearchParams => ({
    __clerk_status: search.__clerk_status as string | undefined,
    __clerk_ticket: search.__clerk_ticket as string | undefined,
    orgResolutionFailed: search.orgResolutionFailed as string | undefined,
    redirect: search.redirect as string | undefined,
  }),
  beforeLoad: ({ context, search }) => {
    // Don't redirect if this is an invitation flow - let component handle conflict
    // Clerk uses sign_up for new users, sign_in for existing users accepting invite
    const isInvitationFlow =
      (search.__clerk_status === 'sign_up' || search.__clerk_status === 'sign_in') &&
      search.__clerk_ticket;
    const isSessionRecovery = Boolean(search.orgResolutionFailed);
    const redirectTarget = getSafeLoginRedirect(search.redirect);
    if (context.auth.isSignedIn && redirectTarget && !isInvitationFlow && !isSessionRecovery) {
      throw redirect({ href: redirectTarget, replace: true });
    }
    // `/` decides the workspace (one salon, the salon picker, or agency) for every signed-in visitor.
    if (context.auth.isSignedIn && !isInvitationFlow && !isSessionRecovery) {
      throw redirect({ to: '/', replace: true });
    }
  },
  component: LoginPage,
});

/**
 * Custom Login Page
 * Design: Boutique Pastel Air — card-less form floating on a soft pastel gradient,
 * fused input block, teal gradient CTA. Matches the Calendar / boutique design language.
 *
 * Signed out: one "Email or phone number" field — email + password, or phone + texted code
 * (`SignInFlow`). Signed in: forwards to the right workspace, or offers recovery.
 *
 * Handles Clerk organization invitation flow:
 * - When __clerk_status=sign_up is present in URL, show the invitation sign-up form
 * - The form processes __clerk_ticket for invitation acceptance
 * - If user is already signed in during invitation, show conflict UI
 */
function LoginPage() {
  const { isLoaded } = useSignIn();
  const { isSignedIn, isLoaded: isAuthLoaded } = useAuth();
  const clerk = useClerk();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });

  // Check if this is an invitation flow (Clerk redirects with these params)
  // Clerk uses sign_up for new users, sign_in for existing users accepting invite
  const isInvitationFlow =
    (search.__clerk_status === 'sign_up' || search.__clerk_status === 'sign_in') &&
    search.__clerk_ticket;
  // 'network' = transient failure (retry recovers); 'not-found' = workspace truly gone.
  const recoveryReason = search.orgResolutionFailed;
  const isSessionRecovery = Boolean(recoveryReason);
  // Page the user was on when the auth guard sent them here (e.g. /399090/inbox).
  const redirectTarget = getSafeLoginRedirect(search.redirect);

  // Session conflict: user is already signed in but trying to accept an invitation
  const hasSessionConflict = isLoaded && isSignedIn && isInvitationFlow;

  // Signed in while on this page (e.g. another tab signed in): back to the page the visitor
  // came from, else the landing route, which picks the salon (or the salon picker / agency).
  // Never the session's last salon directly: that would skip the picker for 2+ salons.
  useEffect(() => {
    if (!isAuthLoaded || !isSignedIn || isInvitationFlow || isSessionRecovery) return;

    if (redirectTarget) {
      navigate({ href: redirectTarget, replace: true });
    } else {
      navigate({ to: '/', replace: true });
    }
  }, [isAuthLoaded, isInvitationFlow, isSessionRecovery, isSignedIn, navigate, redirectTarget]);

  if (isSessionRecovery && isAuthLoaded && isSignedIn) {
    return <WorkspaceRecoveryScreen isWorkspaceGone={recoveryReason === 'not-found'} />;
  }

  // Show session conflict UI when already signed in during invitation
  if (hasSessionConflict) {
    return <InvitationSessionConflictScreen />;
  }

  // If there's an invitation ticket and user is not signed in, show invitation flow
  // Works for both sign_up (new user) and sign_in (existing user accepting invite)
  if (search.__clerk_ticket && !isSignedIn) {
    return (
      <InvitationSignUpForm
        ticket={search.__clerk_ticket}
        onSuccess={() => {
          // After successful signup, Clerk will auto-redirect or we navigate
          const orgId = clerk.session?.lastActiveOrganizationId;
          if (orgId) {
            navigate({ to: '/$orgId', params: { orgId } });
          } else {
            navigate({ to: '/' });
          }
        }}
      />
    );
  }

  return <SignInFlow redirectTarget={redirectTarget} />;
}
